import "./styles.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useBookingForm } from "./useBookingForm.js";
import {
  fetchAllClusters,
  fetchAllVenues,
  fetchAllFacilities,
  fetchTimeSlots,
} from "../../services/adminApiService/venueManagerApi/venueManagerApi.js";

export default function BookingForm({ selectedUser }) {
  const navigate = useNavigate();
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    resetForm,
  } = useBookingForm();
  const [clusters, setClusters] = useState([]);
  const [venues, setVenues] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (
      clusters.length === 1 &&
      String(values.cluster) !== String(clusters[0].id)
    ) {
      handleChange("cluster", String(clusters[0].id));
    }
  }, [clusters, values.cluster, handleChange]);

  useEffect(() => {
    if (venues.length === 1 && String(values.venue) !== String(venues[0].id)) {
      handleChange("venue", String(venues[0].id));
    }
  }, [venues, values.venue, handleChange]);

  useEffect(() => {
    const getClusters = async () => {
      try {
        const res = await fetchAllClusters();
        setClusters(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching clusters:", err);
      }
    };

    getClusters();
  }, []);

  useEffect(() => {
    if (!values.cluster) {
      setVenues([]);
      return;
    }

    const getVenues = async () => {
      try {
        const res = await fetchAllVenues(values.cluster);
        setVenues(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching venues:", err);
      }
    };

    getVenues();
  }, [values.cluster]);

  useEffect(() => {
    if (!values.venue) {
      setFacilities([]);
      return;
    }

    const getFacilities = async () => {
      try {
        const res = await fetchAllFacilities(values.venue);
        setFacilities(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching facilities:", err);
      }
    };

    getFacilities();
  }, [values.venue]);

  const onSubmit = handleSubmit(async (data) => {
    if (participantSlotError) return;

    const updatedData = {
      ...data,
      sport_id: selectedFacility?.facility_type || "", // ✅ inject here
    };

    // ✅ save updated data
    localStorage.setItem("bookingFormData", JSON.stringify(updatedData));
    navigate("/payment-summary", {
      state: {
        bookingType: "existing",
        user: selectedUser,
        sportName: selectedFacility?.facility_name || "",
        zoneName: selectedZone?.zone_name || "",
        slotLabel: selectedSlot
          ? `${selectedSlot.slot_from_time} - ${selectedSlot.slot_to_time} (${selectedSlot.total_slot} left)`
          : "",
        clusterName: selectedCluster?.cluster_name || "",
        venueName: selectedVenue?.venue_name || "",
        venueAddress: selectedVenue?.venue_address || "",
        slotId: values.timeSlot,
        booking: data,
        amount: totalAmount,
        sport_id: selectedFacility?.facility_type || "",
      },
    });
  });

  const hasError = (field) =>
    field === "participants"
      ? Boolean(participantSlotError || (touched[field] && errors[field]))
      : Boolean(touched[field] && errors[field]);
  const inputClass = (field) =>
    hasError(field) ? "booking-input booking-input-error" : "booking-input";

  // Only show error after user has touched the field
  const showError = (field) =>
    field === "participants" && participantSlotError ? (
      <span className="field-error">{participantSlotError}</span>
    ) : hasError(field) ? (
      <span className="field-error">{errors[field]}</span>
    ) : null;

  useEffect(() => {
    if (!values.venue || !values.zone || !values.bookingDate) {
      setTimeSlots([]);
      return;
    }

    const getTimeSlots = async () => {
      try {
        setLoadingSlots(true); // ✅ start loading

        const res = await fetchTimeSlots(
          values.venue,
          selectedFacility?.facility_type,
          values.zone,
          values.bookingDate,
        );

        if (res?.data?.success) {
          setTimeSlots(res?.data?.data?.slots || []);
        } else {
          setTimeSlots([]);
        }
      } catch (error) {
        console.error("Error fetching time slots:", error);
        setTimeSlots([]);
      } finally {
        setLoadingSlots(false); // ✅ stop loading
      }
    };

    getTimeSlots();
  }, [values.venue, values.zone, values.bookingDate]);

  const selectedFacility = facilities.find(
    (f) => String(f.id) === String(values.sport),
  );

  const zones = selectedFacility?.zones || [];

  const selectedSlot = timeSlots.find(
    (slot) => String(slot.id) === String(values.timeSlot),
  );

  const selectedSlotRemaining =
    selectedSlot?.remaining == null ? null : Number(selectedSlot.remaining);
  const participantsValue =
    values.participants === "" || values.participants == null
      ? null
      : Number(values.participants);
  const participantSlotError =
    selectedSlotRemaining !== null &&
    participantsValue !== null &&
    participantsValue > selectedSlotRemaining
      ? `Max allowed ${selectedSlotRemaining} participants.`
      : "";

  const totalAmount =
    selectedSlot && values.participants
      ? Number(selectedSlot.ammount) * Number(values.participants)
    : 0;

  const selectedZone = zones.find(
    (z) => String(z.zone_id) === String(values.zone),
  );

  const selectedCluster = clusters.find(
    (c) => String(c.id) === String(values.cluster),
  );

  const selectedVenue = venues.find(
    (v) => String(v.id) === String(values.venue),
  );

  const selectedSport = facilities.find(
    (f) => String(f.id) === String(values.sport),
  );

  return (
    <form onSubmit={onSubmit}>
      <div className="booking-wrapper">
        <h3 className="booking-title">Booking Details</h3>

        <div className="booking-grid">
          <div className="booking-field">
            <label htmlFor="cluster">
              Cluster <span className="required">*</span>
            </label>
            {/* <select
              id="cluster"
              className={inputClass("cluster")}
              aria-invalid={hasError("cluster")}
              value={values.cluster ?? ""}
              onChange={(e) => handleChange("cluster", e.target.value)}
            >
              <option value="">Select Cluster</option>

              {clusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.cluster_name}
                </option>
              ))}
            </select> */}
            <input
              type="text"
              id="cluster"
              className={inputClass("cluster")}
              value={clusters[0]?.cluster_name || ""}
              disabled
            />
            {/* {showError("cluster")} */}
          </div>

          <div className="booking-field">
            <label htmlFor="venue">
              Venue <span className="required">*</span>
            </label>
            {/* <select
              id="venue"
              value={values.venue ?? ""}
              onChange={(e) => handleChange("venue", e.target.value)}
              className={inputClass("venue")}
              disabled={!values.cluster}
            >
              <option value="">Select Venue</option>

              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.venue_name}
                </option>
              ))}
            </select> */}
            <input
              type="text"
              id="venue"
              className={inputClass("venue")}
              value={venues[0]?.venue_name || ""}
              disabled
            />
            {/* {showError("venue")} */}
            {values.cluster && venues.length === 0 && (
              <span className="field-error">No venue available</span>
            )}
          </div>

          <div className="booking-field">
            <label htmlFor="sport">
              Sport <span className="required">*</span>
            </label>
            <select
              id="sport"
              value={values.sport ?? ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selected = facilities.find(
                  (f) => String(f.id) === String(selectedId),
                );

                handleChange("sport", selectedId);

                // 🔥 store name also
                // handleChange("sportName", selected?.facility_name || "");
              }}
              className={inputClass("sport")}
              disabled={!values.venue || facilities.length == 0}
            >
              <option value="">Select Sport</option>

              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.facility_name}
                </option>
              ))}
            </select>
            {showError("sport")}
            {values.venue && facilities.length === 0 && (
              <span className="field-error">No sport available</span>
            )}
          </div>

          <div className="booking-field">
            <label htmlFor="bookingDate">
              Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="bookingDate"
              className={inputClass("bookingDate")}
              aria-invalid={hasError("bookingDate")}
              value={values.bookingDate ?? ""}
              min={today}
              onChange={(e) => {
                handleChange("bookingDate", e.target.value);
                // e.target.blur();
              }}
            />
            {showError("bookingDate")}
          </div>

          <div className="booking-field">
            <label htmlFor="zone">
              Zone <span className="required">*</span>
            </label>

            <select
              id="zone"
              className={inputClass("zone")}
              value={values.zone ?? ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selected = zones.find(
                  (z) => String(z.zone_id) === String(selectedId),
                );

                handleChange("zone", selectedId);

                // 🔥 store name also
                // handleChange("zoneName", selected?.zone_name || "");
              }}
              disabled={!values.sport || zones.length == 0}
            >
              <option value="">Select Zone</option>

              {zones.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>
                  {zone.zone_name}
                </option>
              ))}
            </select>

            {showError("zone")}
            {values.sport && zones.length === 0 && (
              <span className="field-error">No zone available</span>
            )}
          </div>

          <div className="booking-field">
            <label htmlFor="timeSlot">
              Select Time Slot <span className="required">*</span>
            </label>

            <select
              id="timeSlot"
              className={inputClass("timeSlot")}
              value={values.timeSlot ?? ""}
              onChange={(e) => handleChange("timeSlot", e.target.value)}
              disabled={!timeSlots.length}
            >
              <option value="">Select Available Slot</option>

              {timeSlots.map((slot) => (
                <option
                  key={slot.id}
                  value={slot.id}
                  disabled={slot.total_slot === 0} // 🚫 disable full slots
                >
                  {slot?.slot_from_time} - {slot?.slot_to_time} (
                  {slot?.remaining} left)
                </option>
              ))}
            </select>

            {showError("timeSlot")}

            {/* ✅ API-based validation */}
            {values.zone && values.bookingDate && timeSlots.length === 0 && (
              <span className="field-error">
                No slots available for selected date & zone
              </span>
            )}
          </div>
          <div className="booking-field">
            <label htmlFor="participants">
              No. of Participants <span className="required">*</span>
            </label>
            <input
              type="number"
              id="participants"
              placeholder="Select No. of Participants"
              className={inputClass("participants")}
              aria-invalid={hasError("participants")}
              value={values.participants ?? ""}
              disabled={!timeSlots.length}
              min={1}
              inputMode="numeric" // 📱 mobile numeric keyboard
              onKeyDown={(e) => {
                // 🚫 block invalid keys
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                let raw = e.target.value;

                // ✅ allow empty (so user can delete)
                if (raw === "") {
                  handleChange("participants", "");
                  return;
                }

                // 🚫 remove non-digits (extra safety)
                raw = raw.replace(/[^0-9]/g, "");

                let value = Number(raw);

                // 🚫 prevent 0 or negative
                if (value < 1) value = 1;

                handleChange("participants", value);
              }}
            />
            {/* {selectedSlotRemaining !== null && (
              <small className="booking-helper-text">
                Max allowed: {selectedSlotRemaining}
              </small>
            )} */}
            {showError("participants")}
          </div>
          <div className="guest-field full">
            <label>Amount</label>
            <input
              value={totalAmount ? `₹ ${totalAmount}/-` : "₹ 0/-"}
              readOnly
              className="booking-input"
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="booking-footer">
          <button type="button" className="btn-cancel" onClick={resetForm}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? "Please Wait..." : "Continue"}
          </button>
        </div>
      </div>
    </form>
  );
}
